import { ScenariosCondition } from '@api';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { ScenariosData } from '@terminal/react-query-hooks/scenarios/useScenarios';
import { parseScenarioData } from '@terminal/widgets/scenario/utils/scenario-table-data/scenario-table-data';
import {
  getHorizonColumns,
  getSummaryColumns,
  ScenarioTableData,
} from '@terminal/widgets/scenario/utils/scenario-table-utils/scenario-table-utils';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useScenarioStore } from '../../hooks/use-scenario-store/useScenarioStore';
import { EmptyTable } from '../empty-table/EmptyTable';
import { ScenarioNoResults } from '../scenario-empty-state/ScenarioNoResults';
import { ScenarioNoAssets } from '../scenario-empty-state/scenario-no-assets/ScenarioNoAssets';
import { ScenarioNoConditions } from '../scenario-empty-state/scenario-no-conditions/ScenarioNoConditions';
import { ScenarioNoFilteredAssets } from '../scenario-empty-state/scenario-no-filtered-assets/ScenarioNoFilteredAssets';
import { ScenarioReadyToRun } from '../scenario-empty-state/scenario-ready-to-run/ScenarioReadyToRun';
import { ScenarioUsageExceeded } from '../scenario-empty-state/scenario-usage-exceeded/ScenarioUsageExceeded';
import { ScenarioLoader } from '../scenario-loader/ScenarioLoader';
import { ScenarioTable } from '../scenario-table/ScenarioTable';
import styles from './ScenarioResults.module.scss';

export interface ScenarioResultsProps {
  activeHorizon: string;
  conditionsCount: number;
  data: ScenariosData[];
  disabled: boolean;
  entitiesTags: string[];
  noAssetsButtonLabel?: string;
  hasError: boolean;
  horizons: string[];
  isLoading: boolean;
  isSummary: boolean;
  lastConditions?: ScenariosCondition[];
  onAbort: () => void;
  onEditAssets: () => void;
  onAddConditions?: () => void;
  onRunConditions: () => void;
  onRowClick: (item: ScenarioTableData) => void;
}

export const ScenarioResults = ({
  activeHorizon,
  conditionsCount,
  data,
  disabled,
  entitiesTags,
  noAssetsButtonLabel,
  hasError,
  horizons,
  isLoading,
  isSummary,
  lastConditions,
  onAbort,
  onEditAssets,
  onAddConditions,
  onRowClick,
  onRunConditions,
}: ScenarioResultsProps) => {
  const { t } = useTranslation('scenario');

  const hasActiveAssetFilters = useScenarioStore(
    state => !!state.filterGroups.ASSET_FILTERS.activeFilterOptions.length
  );
  const hasExceededUsageLimit = useScenarioStore(
    state => state.hasExceededUsageLimit
  );

  const hasData = data.some(item => item.predictions.length > 0);
  const hasNoAssets = !entitiesTags.length;
  const hasNoConditions = !conditionsCount;

  if (isLoading) {
    return (
      <ScenarioLoader
        countEntities={entitiesTags.length}
        onAbort={onAbort}
        conditionsCount={conditionsCount}
      />
    );
  }

  if (hasExceededUsageLimit) {
    return (
      <EmptyTable entitiesTags={entitiesTags}>
        <ScenarioUsageExceeded
          assetsCount={entitiesTags.length}
          conditionsCount={conditionsCount}
        />
      </EmptyTable>
    );
  }

  if (hasNoAssets) {
    if (hasActiveAssetFilters) {
      return <ScenarioNoFilteredAssets />;
    } else {
      return (
        <>
          <ScenarioNoAssets
            label={noAssetsButtonLabel}
            onClick={onEditAssets}
            hasNoConditions={hasNoConditions}
          />
        </>
      );
    }
  }

  if (!lastConditions?.length || disabled) {
    const hasConditions = conditionsCount > 0;

    return (
      <EmptyTable entitiesTags={entitiesTags}>
        {hasConditions ? (
          <ScenarioReadyToRun
            assetsCount={entitiesTags.length}
            conditionsCount={conditionsCount}
            runConditionsOnClick={onRunConditions}
          />
        ) : (
          <ScenarioNoConditions addConditionsOnClick={onAddConditions} />
        )}
      </EmptyTable>
    );
  }

  if (!hasData) {
    return (
      <EmptyTable entitiesTags={entitiesTags}>
        <ScenarioNoResults />
      </EmptyTable>
    );
  }

  const parsedData = parseScenarioData({ data, horizons, activeHorizon });
  const columns = isSummary ? getSummaryColumns(t) : getHorizonColumns(t);

  return (
    <div
      className={clsx(styles.scenarioResultsRoot, {
        [styles.disabled]: disabled,
      })}
      data-testid="scenario-results"
    >
      <ErrorBoundaryWrapper showBorder={false}>
        <ScenarioTable
          data={parsedData}
          columns={columns}
          hasError={hasError}
          onRowClick={onRowClick}
        />
      </ErrorBoundaryWrapper>
    </div>
  );
};
